import { useState, useEffect, lazy, Suspense } from 'react'
import { Button } from '../ui/button'
import { Input } from '../ui/input'
import { Label } from '../ui/label'
import { Checkbox } from '../ui/checkbox'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table'
import { mdiPlus, mdiTrashCanOutline } from '@mdi/js'
import { AppIcon } from '../ui/icon'
import { useTheme } from '../../contexts/ThemeContext'

import { javascript } from '@codemirror/lang-javascript'
import { EmptyState } from '../ui/empty-state'
import { useTranslation } from 'react-i18next'

const CodeMirror = lazy(() => import('@uiw/react-codemirror'))

export interface NormalizationOp {
  op: string
  pattern?: string
  replacement?: string
  values?: Record<string, string>
  strict?: boolean
  decimal_places?: number
  integer?: boolean
  script?: string
}

interface OperationEditDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  operation: NormalizationOp | null
  onSave: (op: NormalizationOp) => void
}

const OP_DESCRIPTIONS: Record<string, string> = {
  trim: 'Remove leading/trailing whitespace',
  lowercase: 'Convert to lowercase',
  uppercase: 'Convert to uppercase',
  regex_replace: 'Replace text matching a regular expression',
  map: 'Dictionary lookup — map input values to output values',
  number_parse: 'Parse as a number',
  boolean_parse: 'Parse as a boolean (true/false, yes/no, 1/0)',
  script: 'Run custom JavaScript to transform the value',
}

export function OperationEditDialog({ open, onOpenChange, operation, onSave }: OperationEditDialogProps) {
  const { t } = useTranslation('derivations')
  const { theme } = useTheme()
  const [op, setOp] = useState<NormalizationOp>({ op: 'trim' })

  useEffect(() => {
    if (operation) {
      setOp({ ...operation })
    }
  }, [operation])

  const handleSave = () => {
    // Clean up fields not relevant to the operation
    const cleaned: NormalizationOp = { op: op.op }
    switch (op.op) {
      case 'regex_replace':
        cleaned.pattern = op.pattern || ''
        cleaned.replacement = op.replacement || ''
        break
      case 'map':
        cleaned.values = op.values || {}
        if (op.strict) cleaned.strict = true
        break
      case 'number_parse':
        if (op.decimal_places !== undefined && op.decimal_places >= 0) cleaned.decimal_places = op.decimal_places
        if (op.integer) cleaned.integer = true
        break
      case 'script':
        cleaned.script = op.script || ''
        break
    }
    onSave(cleaned)
    onOpenChange(false)
  }

  const handleAddMapEntry = () => {
    const values = { ...(op.values || {}), '': '' }
    setOp({ ...op, values })
  }

  const handleRemoveMapEntry = (key: string) => {
    const values = { ...(op.values || {}) }
    delete values[key]
    setOp({ ...op, values })
  }

  const handleMapKeyChange = (oldKey: string, newKey: string) => {
    const values = { ...(op.values || {}) }
    const val = values[oldKey]
    delete values[oldKey]
    values[newKey] = val || ''
    setOp({ ...op, values })
  }

  const handleMapValueChange = (key: string, value: string) => {
    const values = { ...(op.values || {}), [key]: value }
    setOp({ ...op, values })
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {operation ? 'Edit Operation' : 'Add Operation'}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          {/* Operation type description */}
          <div className="text-sm text-muted-foreground">
            {OP_DESCRIPTIONS[op.op] || op.op}
          </div>

          {/* Operation-specific fields */}
          {op.op === 'regex_replace' && (
            <>
              <div>
                <Label htmlFor="pattern">Pattern (regex)</Label>
                <Input
                  id="pattern"
                  value={op.pattern || ''}
                  onChange={(e) => setOp({ ...op, pattern: e.target.value })}
                  className="font-mono"
                  placeholder="e.g., \\s+"
                  data-testid="op-edit-pattern"
                />
              </div>
              <div>
                <Label htmlFor="replacement">Replacement</Label>
                <Input
                  id="replacement"
                  value={op.replacement || ''}
                  onChange={(e) => setOp({ ...op, replacement: e.target.value })}
                  className="font-mono"
                  placeholder="e.g., _"
                  data-testid="op-edit-replacement"
                />
              </div>
            </>
          )}

          {op.op === 'map' && (
            <>
              <div className="flex items-center gap-2">
                <Checkbox
                  checked={op.strict || false}
                  onCheckedChange={(checked) => setOp({ ...op, strict: checked === true })}
                  data-testid="op-edit-strict"
                />
                <Label className="text-sm">Strict mode (reject unmatched values)</Label>
              </div>
              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label>Value Mappings</Label>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleAddMapEntry}
                    data-testid="op-edit-add-mapping"
                  >
                    <AppIcon path={mdiPlus} size="xs" className="mr-1" />
                    Add Entry
                  </Button>
                </div>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Key (input)</TableHead>
                      <TableHead>Value (output)</TableHead>
                      <TableHead className="w-12" />
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {Object.entries(op.values || {}).map(([key, value], idx) => (
                      <TableRow key={idx}>
                        <TableCell className="p-1">
                          <Input
                            value={key}
                            onChange={(e) => handleMapKeyChange(key, e.target.value)}
                            className="font-mono text-sm"
                            placeholder="input value"
                          />
                        </TableCell>
                        <TableCell className="p-1">
                          <Input
                            value={value}
                            onChange={(e) => handleMapValueChange(key, e.target.value)}
                            className="font-mono text-sm"
                            placeholder="output value"
                          />
                        </TableCell>
                        <TableCell className="p-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            onClick={() => handleRemoveMapEntry(key)}
                          >
                            <AppIcon path={mdiTrashCanOutline} size="xs" className="text-destructive" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                    {Object.keys(op.values || {}).length === 0 && (
                      <TableRow>
                        <TableCell colSpan={3}>
                          <EmptyState variant="inline" className="py-4" title={t('normalizations.no_mappings')} />
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            </>
          )}

          {op.op === 'number_parse' && (
            <>
              <div className="flex items-center gap-2">
                <Checkbox
                  checked={op.integer || false}
                  onCheckedChange={(checked) => setOp({ ...op, integer: checked === true })}
                  data-testid="op-edit-integer"
                />
                <Label className="text-sm">Integer only</Label>
              </div>
              <div className="w-48">
                <Label htmlFor="decimal-places">Decimal Places</Label>
                <Input
                  id="decimal-places"
                  type="number"
                  min={0}
                  value={op.decimal_places ?? ''}
                  onChange={(e) => setOp({ ...op, decimal_places: e.target.value ? parseInt(e.target.value) : undefined })}
                  placeholder="Auto"
                  data-testid="op-edit-decimal-places"
                />
              </div>
            </>
          )}

          {op.op === 'script' && (
            <div>
              <Label>JavaScript Code</Label>
              <p className="text-xs text-muted-foreground mb-2">
                The variable <code className="bg-muted px-1 rounded">value</code> contains the input.
                Use <code className="bg-muted px-1 rounded">return</code> to output the result.
                Call <code className="bg-muted px-1 rounded">reject()</code> or
                <code className="bg-muted px-1 rounded"> invalid()</code> to set status.
                Helpers: <code className="bg-muted px-1 rounded">toNumber()</code>,
                <code className="bg-muted px-1 rounded"> toBoolean()</code>,
                <code className="bg-muted px-1 rounded"> slugify()</code>,
                <code className="bg-muted px-1 rounded"> stripHtml()</code>.
              </p>
              <Suspense fallback={<div className="h-[200px] border border-input rounded-md flex items-center justify-center text-muted-foreground text-sm">Loading editor...</div>}>
                <CodeMirror
                  value={op.script || ''}
                  onChange={(val: string) => setOp({ ...op, script: val })}
                  extensions={[javascript()]}
                  height="200px"
                  maxHeight="400px"
                  placeholder={'if (!value) return null;\nreturn value.trim().toUpperCase();'}
                  className="rounded-md border border-input overflow-hidden"
                  theme={theme === 'dark' ? 'dark' : 'light'}
                  data-testid="op-edit-script"
                />
              </Suspense>
              <p className="text-xs text-muted-foreground mt-1">
                {(op.script || '').length} / 4096 characters
              </p>
            </div>
          )}

          {/* Simple ops (trim, lowercase, uppercase, boolean_parse) have no extra fields */}
          {['trim', 'lowercase', 'uppercase', 'boolean_parse'].includes(op.op) && (
            <div className="text-sm text-muted-foreground italic py-2">
              This operation has no additional configuration.
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={handleSave} data-testid="op-edit-save">Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
